import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { MetricRange } from '../services/syntax-api.service';
import type { NetworkAnalysisIndicatorId } from '../config/network-analysis-content';

export interface NetworkAnalysisUiState {
  analysisActive: boolean;
  activeIndicator: NetworkAnalysisIndicatorId;
  structureVisualRangeOverride: MetricRange | null;
  structureContrastExponent: number;
}

export interface NetworkAnalysisUiActions {
  setAnalysisActive: (active: boolean) => void;
  setActiveIndicator: (indicator: NetworkAnalysisIndicatorId) => void;
  setStructureVisualRangeOverride: (range: MetricRange | null) => void;
  resetStructureVisualRangeOverride: () => void;
  setStructureContrastExponent: (value: number) => void;
  reset: () => void;
}

export type NetworkAnalysisUiStore = NetworkAnalysisUiState & NetworkAnalysisUiActions;

const initialState: NetworkAnalysisUiState = {
  analysisActive: false,
  activeIndicator: 'integration_global',
  structureVisualRangeOverride: null,
  structureContrastExponent: 1,
};

export const useNetworkAnalysisUiStore = create<NetworkAnalysisUiStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      setAnalysisActive: (analysisActive: boolean): void => {
        if (get().analysisActive === analysisActive) {
          return;
        }

        set({ analysisActive });
      },
      setActiveIndicator: (activeIndicator: NetworkAnalysisIndicatorId): void => {
        if (get().activeIndicator === activeIndicator) {
          return;
        }

        set({ activeIndicator });
      },
      setStructureVisualRangeOverride: (structureVisualRangeOverride: MetricRange | null): void => {
        set({ structureVisualRangeOverride });
      },
      resetStructureVisualRangeOverride: (): void => {
        if (!get().structureVisualRangeOverride) {
          return;
        }

        set({ structureVisualRangeOverride: null });
      },
      setStructureContrastExponent: (structureContrastExponent: number): void => {
        if (get().structureContrastExponent === structureContrastExponent) {
          return;
        }

        set({ structureContrastExponent });
      },
      reset: (): void => {
        set(initialState);
      },
    }),
    { name: 'network-analysis-ui-store' },
  ),
);

export const selectActiveNetworkAnalysisIndicator = (
  state: NetworkAnalysisUiStore,
): NetworkAnalysisIndicatorId => state.activeIndicator;
